import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Stack,
  Avatar,
  Card,
  CardContent,
  Box,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import { useCollection } from "../hooks/useCollection";
import OtherProductDialog from "../components/OtherProductDialog";
import { OtherProduct } from "../interfaces/OtherProduct";
import { useAuth } from "../hooks/useAuth";
import { InventoryItem } from "../interfaces/Inventory";
import { isDrinkCategory } from "../utils/drinkUtils";

export default function DrinksPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    data: allProducts,
    createItem,
    updateItem,
    deleteItem,
  } = useCollection<OtherProduct>(restaurantId ?? "", "otherProducts");

  const { data: categories = [] } = useCollection<{ id: string; name: string }>(
    restaurantId ?? "",
    "category"
  );

  const { data: inventoryItems = [] } = useCollection<InventoryItem>(
    restaurantId ?? "",
    "inventory"
  );

  const { canCreate, canEdit, canDelete } = useAuth();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<OtherProduct | null>(null);

  /* ------------------ FILTER STATE ------------------ */
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Filtrar productos que sean bebidas
  const drinksList = allProducts.filter((p) => isDrinkCategory(p.category));

  const filteredDrinks = drinksList.filter((p) => {
    const matchesName = p.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || p.category === selectedCategory;
    return matchesName && matchesCategory;
  });

  // Extraer las categorías de bebidas existentes en la base de datos
  const drinkCategories = Array.from(
    new Set(drinksList.map((d) => d.category).filter(Boolean))
  );

  // Categorías de bebidas a mostrar en el modal de creación
  const availableDrinkCategories = categories.filter((c) => isDrinkCategory(c.name));
  const dialogCategories = availableDrinkCategories.length > 0
    ? availableDrinkCategories
    : [
        { id: "cat_bebidas", name: "Bebidas" },
        { id: "cat_gaseosas", name: "Gaseosas" },
        { id: "cat_jugos", name: "Jugos" },
        { id: "cat_cervezas", name: "Cervezas" },
        { id: "cat_aguas", name: "Aguas" },
      ];

  /* ------------------ DESKTOP COLUMNS ------------------ */
  const columns: GridColDef[] = [
    {
      field: "image",
      headerName: "Imagen",
      width: 100,
      renderCell: (params) => (
        <Avatar
          src={params.row.image}
          variant="rounded"
          sx={{ width: 52, height: 52, borderRadius: 2 }}
        >
          🥤
        </Avatar>
      ),
    },
    { field: "name", headerName: "Nombre de la Bebida", flex: 1.5 },
    {
      field: "category",
      headerName: "Categoría",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value || "Bebidas"}
          size="small"
          color="primary"
          variant="filled"
        />
      ),
    },
    {
      field: "inventoryLink",
      headerName: "Inventario Vinculado",
      flex: 1.2,
      renderCell: (params) => {
        const product = params.row as OtherProduct;
        const invId = product.inventoryItemId;
        if (!invId)
          return (
            <Typography variant="caption" color="text.disabled">
              No vinculado
            </Typography>
          );

        const invItem = inventoryItems.find((i) => i.id === invId);
        return (
          <Chip
            label={invItem?.name || "ID: " + invId.substring(0, 6)}
            size="small"
            variant="outlined"
            color={invItem ? "success" : "default"}
          />
        );
      },
    },
    {
      field: "price",
      headerName: "Precio Base",
      width: 130,
      renderCell: (params) => {
        const prod = params.row as OtherProduct;
        return (
          <Typography variant="body2" fontWeight={700} color="success.main">
            ${(prod.price ?? 0).toLocaleString("es-CO")}
          </Typography>
        );
      },
    },
    {
      field: "variants",
      headerName: "Variaciones / Presentaciones",
      flex: 1.8,
      renderCell: (params) => {
        const prod = params.row as OtherProduct;
        if (!prod.variants || prod.variants.length === 0) {
          return (
            <Typography variant="caption" color="text.disabled">
              Sin presentaciones adicionales
            </Typography>
          );
        }
        return (
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ py: 1, my: "auto" }}
          >
            {prod.variants.map((v) => (
              <Chip
                key={v.id || v.name}
                label={`${v.name}: $${((prod.price ?? 0) + (v.price ?? 0)).toLocaleString("es-CO")}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: "0.72rem", height: 22 }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={!canEdit}
            onClick={() => {
              setCurrent(params.row as OtherProduct);
              setOpen(true);
            }}
          >
            Editar
          </Button>
          <Button
            size="small"
            color="error"
            disabled={!canDelete}
            onClick={() => {
              if (
                window.confirm(
                  `¿Estás seguro de que deseas eliminar la bebida "${params.row.name}"?`
                )
              ) {
                deleteItem(params.row.id);
              }
            }}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} display="flex" alignItems="center" gap={1}>
            🥤 Módulo Exclusivo de Bebidas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra gaseosas, jugos, cervezas y bebidas del menú
          </Typography>
        </Box>

        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<LocalBarIcon />}
            onClick={() => {
              setCurrent(null);
              setOpen(true);
            }}
            sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
          >
            Agregar Bebida
          </Button>
        )}
      </Stack>

      {/* FILTROS Y BÚSQUEDA */}
      <Card sx={{ mb: 3, borderRadius: 3, p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Buscar bebida por nombre..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} overflow="auto" maxWidth="100%">
            <Chip
              label="Todas"
              color={selectedCategory === "all" ? "primary" : "default"}
              onClick={() => setSelectedCategory("all")}
              clickable
            />
            {drinkCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                color={selectedCategory === cat ? "primary" : "default"}
                onClick={() => setSelectedCategory(cat)}
                clickable
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      {/* VISTA DESKTOP */}
      {!isMobile && (
        <Box sx={{ height: 540 }}>
          <PremiumDataGrid
            rows={filteredDrinks}
            columns={columns}
            getRowId={(row) => row.id!}
            onRowDoubleClick={(p: GridRowParams) => {
              setCurrent(p.row as OtherProduct);
              setOpen(true);
            }}
          />
        </Box>
      )}

      {/* VISTA MÓVIL */}
      {isMobile && (
        <Stack spacing={2}>
          {filteredDrinks.map((item) => {
            const invItem = inventoryItems.find((i) => i.id === item.inventoryItemId);
            return (
              <Card key={item.id} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={item.image}
                      variant="rounded"
                      sx={{ width: 64, height: 64, borderRadius: 2 }}
                    >
                      🥤
                    </Avatar>
                    <Box flex={1}>
                      <Typography fontWeight={700}>{item.name}</Typography>
                      <Stack direction="row" spacing={1} my={0.5}>
                        <Chip label={item.category || "Bebidas"} size="small" color="primary" />
                        {invItem && (
                          <Chip label={`📦 ${invItem.name}`} size="small" variant="outlined" color="success" />
                        )}
                      </Stack>
                      <Typography variant="body2" fontWeight={800} color="success.main">
                        ${(item.price ?? 0).toLocaleString("es-CO")}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      {canEdit && (
                        <IconButton
                          onClick={() => {
                            setCurrent(item);
                            setOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          color="error"
                          onClick={() => {
                            if (
                              window.confirm(
                                `¿Estás seguro de que deseas eliminar la bebida "${item.name}"?`
                              )
                            ) {
                              deleteItem(item.id!);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* DIÁLOGO CREAR / EDITAR BEBIDA */}
      <OtherProductDialog
        open={open}
        onClose={() => setOpen(false)}
        initial={
          current || {
            name: "",
            price: 0,
            image: "",
            description: "",
            category: dialogCategories[0]?.name || "Bebidas",
            active: true,
            variants: [],
          }
        }
        categories={dialogCategories}
        onSubmit={async (values) => {
          const formattedCategory = values.category && isDrinkCategory(values.category)
            ? values.category
            : (dialogCategories[0]?.name || "Bebidas");

          const payload = {
            ...values,
            category: formattedCategory,
          };

          if (current?.id) await updateItem(current.id, payload);
          else await createItem(payload);
          setOpen(false);
        }}
      />
    </>
  );
}
